import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getCoaches } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, UserPlus, Activity, TrendingUp } from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCoaches();
  }, []);

  const loadCoaches = async () => {
    try {
      const data = await getCoaches();
      setCoaches(data);
    } catch (error) {
      console.error('Error loading coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'Total Coaches', value: coaches.length, icon: Users, color: 'bg-blue-500' },
    { label: 'Coaches Activos', value: coaches.filter(c => c.activo).length, icon: Activity, color: 'bg-green-500' },
    { label: 'Este Mes', value: coaches.length, icon: TrendingUp, color: 'bg-sky-500' },
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-heading text-3xl sm:text-4xl text-slate-800">
              Panel de Administración
            </h1>
            <p className="text-slate-500 mt-1">
              Bienvenido de nuevo, {user?.nombre}
            </p>
          </div>
          <Link to="/admin/coaches">
            <Button className="btn-primary flex items-center gap-2" data-testid="manage-coaches-btn">
              <UserPlus className="w-4 h-4" />
              Gestionar Coaches
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 animate-stagger">
          {stats.map((stat, index) => (
            <Card key={index} className="glass-card card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <stat.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="label-uppercase">{stat.label}</p>
                    <p className="text-heading text-3xl text-slate-800 mt-1">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Coaches */}
        <Card className="glass-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-heading text-xl text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-900" />
              Coaches Recientes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Cargando...</div>
            ) : coaches.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-slate-500 mb-4">No hay coaches registrados</p>
                <Link to="/admin/coaches">
                  <Button className="btn-secondary">Agregar Coach</Button>
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {coaches.slice(0, 5).map((coach) => (
                  <div 
                    key={coach.id_coach} 
                    className="p-4 flex items-center justify-between table-row-hover"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-900 to-sky-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {coach.nombre?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{coach.nombre}</p>
                        <p className="text-sm text-slate-500">{coach.email}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      coach.activo 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      {coach.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
